import { useState, useEffect, useCallback } from 'react';
import { api } from '../utils/api';
import type { CEFRLevel } from '@homeapp/shared';

interface LearningProfile {
  userId: string;
  currentLevel: CEFRLevel;
  xp: number;
  streak: number;
  lastSessionAt: string;
  stats: Record<string, { correct: number; total: number }>;
}

interface ProgressInfo {
  nextLevel: CEFRLevel;
  xpNeeded: number;
  xpProgress: number;
}

interface Exercise {
  exerciseId: string;
  type: 'scramble' | 'fill-blank' | 'free-production' | 'conversation';
  level: number;
  language: string;
  topic: string;
  prompt: string;
  hints?: string[];
  words?: string[];
  template?: string;
  blanks?: string[];
}

interface AnswerResult {
  correct: boolean;
  partiallyCorrect?: boolean;
  feedback: string;
  correctAnswer: string;
  xpEarned: number;
  newXp: number;
  levelUp?: CEFRLevel;
}

export function useLearning() {
  const [profile, setProfile] = useState<LearningProfile | null>(null);
  const [progress, setProgress] = useState<ProgressInfo | null>(null);
  const [exercise, setExercise] = useState<Exercise | null>(null);
  const [loading, setLoading] = useState(true);
  const [exerciseLoading, setExerciseLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchProfile = useCallback(async () => {
    try {
      const res = await api.get<{ profile: LearningProfile; progress: ProgressInfo }>('/learning/profile');
      setProfile(res.profile);
      setProgress(res.progress);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load profile');
    }
  }, []);

  const fetchExercise = useCallback(async () => {
    setExerciseLoading(true);
    try {
      const res = await api.get<{ exercise: Exercise | null }>('/learning/exercise?lang=dutch');
      setExercise(res.exercise);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load exercise');
    } finally {
      setExerciseLoading(false);
    }
  }, []);

  useEffect(() => {
    Promise.all([fetchProfile(), fetchExercise()]).finally(() => setLoading(false));
  }, [fetchProfile, fetchExercise]);

  const submitAnswer = async (exerciseId: string, answer: string): Promise<AnswerResult> => {
    const res = await api.post<AnswerResult>(`/learning/exercise/${exerciseId}/answer`, { answer });
    // Refresh profile after answer
    await fetchProfile();
    return res;
  };

  const nextExercise = async () => {
    await fetchExercise();
  };

  return {
    profile, progress, exercise, loading, exerciseLoading, error,
    submitAnswer, nextExercise, refresh: fetchProfile,
  };
}
